import { useRef, JSX, useState, useContext } from 'react';
import { Menu } from 'primereact/menu';
import ThreeDotsIcon from 'src/assets/icons/three-dots.svg';
import styles from './DocumentsGrid.module.scss';
import { IChecklistItem, IDocument } from 'src/shared/types/Documents';
import downloadIcon from 'src/assets/icons/download-simple.svg';
import unlinkIcon from 'src/assets/icons/arrows-clockwise.svg';
import printIcon from 'src/assets/icons/print.svg';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { Toast } from '@ds/elements';
import { FileUtil } from 'src/utils/FileUtils';
import {
  getDocumentData,
  removePassportDocuments,
  unlinkDocumentsFromChecklistItem,
} from 'src/shared/services/document-api/Documents';
import {
  DocumentPrintingPagePlaceholder,
  DocumentsDownloadType,
  SUPPORTED_PRINT_FILE_TYPES,
} from 'src/utils/constants/common';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { DocumentResponse } from 'src/shared/types/Document';
interface IGridItemMenuProps {
  document: IDocument | File | DocumentResponse;
  currentItem?: IChecklistItem;
  isReadonly?: boolean;
  fileIndex: number;
  isPassport?: boolean;
  onRefetchDocuments?: () => void | Promise<void>;
  onRefetchAdditionalDocuments?: () => void | Promise<void>;
  // New props for file deletion
  isFile?: boolean;
  onDeleteFile?: (fileIndex: number) => void;
}

interface MenuItem {
  label: string;
  icon: JSX.Element;
  target: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  command: () => void | Promise<void>;
}

export const GridItemMenu = (props: IGridItemMenuProps) => {
  const {
    document,
    currentItem,
    onRefetchDocuments,
    isReadonly,
    onRefetchAdditionalDocuments,
    isFile,
    onDeleteFile,
    fileIndex,
  } = props;
  const { data: user } = useGetUser();
  const { caseId, beneId } = useContext(BeneDocAdditionalContext);
  const menu = useRef<Menu>(null);
  const toastRef = useRef<IToastr>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const unlinkDocument = async () => {
    if (!currentItem) return;

    if (!currentItem) return;
    if (!('docId' in document)) return;
    try {
      const docIdToUnlink: number = Number(document.docId);
      const checklistItemId =
        currentItem.checklistItemDocuments?.length > 0 ? currentItem.checklistItemDocuments[0].checklistItemId : 0;

      const unlinkDocRequest = {
        checklistItemId,
        documentId: docIdToUnlink,
        caseId: caseId as number,
      };

      await unlinkDocumentsFromChecklistItem([unlinkDocRequest]);

      try {
        if (onRefetchDocuments) {
          await onRefetchDocuments();
        }
        if (onRefetchAdditionalDocuments) {
          await onRefetchAdditionalDocuments();
        }
      } catch (error) {
        throw new Error('Error refreshing documents. Please refresh the page');
      }
    } catch (error) {
      throw new Error('Failed to unlink document.');
    }
  };

  const isPrintDisabled = () => {
    if (!('documentFiles' in document) || !document.documentFiles || document.documentFiles.length === 0) {
      return true;
    }
    return document.documentFiles.some((file: any) => {
      const fileExtension = file.fileName?.split('.').pop()?.toLowerCase();
      return !SUPPORTED_PRINT_FILE_TYPES.includes(fileExtension || '');
    });
  };

  const handlePrint = async () => {
    if (!('documentApiKey' in document)) throw new Error("Can't print document.");
    try {
      const obj = {
        downloadType: DocumentsDownloadType.Print,
        documentKeys: [document.documentApiKey],
        beneId: beneId,
        userId: user?.userId,
        toastRef,
      };
      const fileDataPromise = getDocumentData(obj);
      await FileUtil.previewArrayBuffer(fileDataPromise, DocumentPrintingPagePlaceholder);
    } catch (error) {
      throw new Error("Can't print document.");
    }
  };

  const handleDownload = async () => {
    if (!('documentApiKey' in document)) throw new Error("Can't download document.");
    try {
      const obj = {
        downloadType: DocumentsDownloadType.Default,
        documentKeys: [document.documentApiKey],
        beneId: beneId,
        toastRef,
      };
      const { arrayBuffer, fileName, type } = await getDocumentData(obj);
      FileUtil.downloadArrayBuffer(arrayBuffer, fileName, type);
    } catch (error) {
      throw new Error("Can't download document.");
    }
  };

  const handleDelete = async () => {
    if (!('docId' in document)) throw new Error("Can't delete document.");
    if (!caseId) {
      throw new Error('caseId is undefined.');
    }
    if (!currentItem || !currentItem.checklistItemDocuments || currentItem.checklistItemDocuments.length === 0) {
      throw new Error('Checklist item is undefined.');
    }
    try {
      const docToDelete = {
        caseId: caseId,
        checklistItemId: currentItem.checklistItemDocuments[0].checklistItemId,
        documentId: parseInt(document.docId?.toString() || '0'),
      };
      const result = await removePassportDocuments(docToDelete);
      if (result?.error || result?.status === false) {
        throw new Error(result.errorMessage || "Can't delete document.");
      }
      onRefetchDocuments && (await onRefetchDocuments());
    } catch (error) {
      throw new Error("Can't delete document.");
    }
  };

  // File delete handler for file type
  const handleFileDelete = async () => {
    if (onDeleteFile) {
      onDeleteFile(fileIndex);
    }
  };

  const handleAction = async (action: () => void | Promise<void>) => {
    let hasError = false;

    if (!isProcessing) {
      setIsProcessing(true);
      toastRef.current?.show({ message: 'Processing...', severity: 'info', sticky: true });
      try {
        await action();
      } catch (error) {
        hasError = true;
        const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
        toastRef.current?.clear();
        toastRef.current?.show({ message: errorMessage, severity: 'error', sticky: true });
      } finally {
        setIsProcessing(false);
        if (!hasError) {
          toastRef.current?.clear();
          toastRef.current?.show({ message: 'Success', severity: 'success', sticky: false });
        }
      }
    }
  };

  const buildMenuModel = () => {
    const baseMenuItems: MenuItem[] = [
      {
        label: 'Print',
        icon: <img src={printIcon} />,
        target: '_blank',
        disabled: isProcessing || isPrintDisabled(),
        command: () => handleAction(handlePrint),
        id: 'sb-document-print-btn',
        className: 'sb-clickable-menu-item',
      },
      {
        label: 'Download',
        icon: <img src={downloadIcon} />,
        target: '_blank',
        disabled: isProcessing,
        command: () => handleAction(handleDownload),
        id: 'sb-document-download-btn',
        className: 'sb-clickable-menu-item',
      },
    ];

    const deleteItem: MenuItem = {
      label: 'Delete',
      icon: <div className={styles['delete-icon']} />,
      target: '_blank',
      disabled: isProcessing,
      command: () => (isFile ? handleFileDelete() : handleAction(handleDelete)),
      id: 'grid-file-delete-btn',
      className: 'sb-clickable-menu-item',
    };

    // If this is a file (not IDocument), only show delete
    if (isFile) {
      return [deleteItem];
    }

    if (isReadonly) {
      return baseMenuItems;
    }

    if (props.isPassport) {
      return [deleteItem, ...baseMenuItems];
    }

    const menuItemToUnlinkDoc: MenuItem = {
      label: 'Unlink from here',
      icon: <img src={unlinkIcon} />,
      target: '_blank',
      disabled: isProcessing,
      command: () => handleAction(unlinkDocument),
      id: 'sb-document-unlink-btn',
      className: 'sb-clickable-menu-item',
    };
    return [menuItemToUnlinkDoc, ...baseMenuItems];
  };

  return (
    <div className={styles['menu-items-container']}>
      <div className="component-grid-item-menu">
        <>
          <Menu popup ref={menu} model={buildMenuModel()} className={styles['popup-menu']} />
          <img
            src={ThreeDotsIcon}
            className={styles['dots-three-icon']}
            onClick={(e) => {
              menu.current && menu.current.toggle(e);
            }}
          />
        </>
      </div>
      <Toast ref={toastRef} />
    </div>
  );
};
